import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Edit, Plus, Trash2, Check } from 'lucide-react';
import SignatureCaptureDialog from '../installer/SignatureCaptureDialog';
import SignatureImage from '../shared/SignatureImage';

const Orders = ({ user }) => {
  // Format date without timezone conversion (for date-only fields)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab || 'pending';
  });
  const [products, setProducts] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    installationDate: '',
    storeInvoiceNumber: '',
    items: [],
    adminNotes: '',
    assignedInstallerId: ''
  });

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchInstallers();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, activeTab]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setInstallers((response.data || []).filter(u => u.role === 'installer'));
    } catch (error) {
      toast.error('Failed to load installers');
    }
  };

  const filterOrders = () => {
    if (activeTab === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(o => o.status === activeTab));
    }
  };

  const handleEditOrder = (order) => {
    setEditFormData({
      customerName: order.customer.name,
      customerEmail: order.customer.email,
      customerPhone: order.customer.phone,
      customerAddress: order.customer.address,
      installationDate: order.installationDate,
      storeInvoiceNumber: order.storeInvoiceNumber || '',
      items: order.items.map(item => ({ ...item })),
      adminNotes: order.adminNotes || '',
      assignedInstallerId: order.assignedInstallerId || ''
    });
    setSelectedOrder(order);
    setEditMode(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, editFormData, { withCredentials: true });
      toast.success('Order updated successfully');
      setEditMode(false);
      fetchOrders();
      // Refresh selected order
      const response = await axios.get(`${API}/orders/${selectedOrder.id}`, { withCredentials: true });
      setSelectedOrder(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/approve`, {}, { withCredentials: true });
      toast.success('Order approved successfully');
      fetchOrders();
      setSelectedOrder(null);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to approve order');
    }
  };

  const handleReject = async (orderId) => {
    const reason = prompt('Please enter rejection reason:');
    if (!reason) return;

    try {
      await axios.post(`${API}/orders/${orderId}/reject?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      toast.success('Order rejected');
      fetchOrders();
      setSelectedOrder(null);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to reject order');
    }
  };

  const handleMarkCompleted = async (orderId) => {
    if (user?.role === 'admin') {
      try {
        await axios.put(`${API}/orders/${orderId}`, { status: 'completed' }, { withCredentials: true });
        toast.success('Order marked as completed');
        fetchOrders();
        if (selectedOrder?.id === orderId) {
          const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
          setSelectedOrder(response.data);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to mark order as completed');
      }
      return;
    }

    const order = orders.find((o) => o.id === orderId) || selectedOrder;
    if (!order) {
      toast.error('Order not found');
      return;
    }
    setSelectedOrder(order);
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = async ({ signatureBlob, customerName }) => {
    if (!selectedOrder?.id) return;

    setSavingSignature(true);
    try {
      const formData = new FormData();
      formData.append('orderId', selectedOrder.id);
      formData.append('signature', signatureBlob, `${selectedOrder.id}-signature.png`);
      if (customerName) {
        formData.append('customerName', customerName);
      }

      const uploadResponse = await axios.post(`${API}/upload-signature`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const signatureUrl = uploadResponse?.data?.url;
      if (!signatureUrl) {
        throw new Error('Missing signature URL from upload response');
      }

      await axios.put(`${API}/orders/${selectedOrder.id}`, {
        status: 'completed',
        signatureUrl,
        signatureKey: uploadResponse?.data?.key || null,
        signatureCustomerName: customerName || null
      }, { withCredentials: true });

      toast.success('Order marked as completed');
      fetchOrders();
      if (selectedOrder?.id) {
        const response = await axios.get(`${API}/orders/${selectedOrder.id}`, { withCredentials: true });
        setSelectedOrder(response.data);
      }
      setSignatureDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to mark order as completed');
    } finally {
      setSavingSignature(false);
    }
  };

  const addItem = () => {
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { productId: '', serviceId: '', name: '', price: 0, spiff: 0, qty: 1, notes: '' }]
    });
  };

  const removeItem = (index) => {
    setEditFormData({
      ...editFormData,
      items: editFormData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index][field] = value;

    if (field === 'serviceId' && value) {
      const product = products.find(p => p.id === newItems[index].productId);
      if (product) {
        const service = product.services.find(s => s.id === value);
        if (service) {
          newItems[index].name = service.name;
          newItems[index].price = service.price;
          newItems[index].spiff = service.spiff;
          if (typeof service.notes === 'string' && service.notes.length > 0) {
            newItems[index].notes = service.notes;
          }
        }
      }
    }

    setEditFormData({ ...editFormData, items: newItems });
  };

  const getBadgeClass = (status) => {
    const classes = {
      pending: 'badge-pending',
      approved: 'badge-approved',
      rejected: 'badge-rejected',
      in_process: 'badge-in-process'
    };
    return classes[status] || 'badge-pending';
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '32px' }}>Manage Orders</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '8px' }}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="admin-tab-pending">Pending ({orders.filter(o => o.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="in_process" data-testid="admin-tab-in-process">In Process</TabsTrigger>
            <TabsTrigger value="approved" data-testid="admin-tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="admin-tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="completed" data-testid="admin-tab-completed">Completed ({orders.filter(o => o.status === 'completed').length})</TabsTrigger>
            <TabsTrigger value="all" data-testid="admin-tab-all">All Orders</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeTab}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Sales Person</th>
                  <th>Customer</th>
                  <th>Installation Date</th>
                  <th>Installer</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.id} data-testid={`admin-order-row-${order.id}`}>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{order.id.substring(0, 8)}</td>
                    <td>
                      {order.createdByName}
                      {order.orderType === 'custom' && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', padding: '2px 6px', background: '#8B5CF6', color: 'white', borderRadius: '4px' }}>CUSTOM</span>
                      )}
                    </td>
                    <td>{order.customer.name}</td>
                    <td>{formatDate(order.installationDate)}</td>
                    <td>{order.assignedInstallerName || '—'}</td>
                    <td style={{ fontWeight: '600' }}>{order.totals.total !== null ? `$${order.totals.total}` : 'Pending'}</td>
                    <td>
                      <span className={`badge ${getBadgeClass(order.status)}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                        onClick={() => setSelectedOrder(order)}
                        data-testid={`admin-view-order-${order.id}`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
                No orders found.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedOrder} onOpenChange={() => { setSelectedOrder(null); setEditMode(false); }}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle>
              {editMode ? 'Edit Order' : 'Order Details'} - {selectedOrder?.id.substring(0, 8)}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && !editMode && (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className={`badge ${getBadgeClass(selectedOrder.status)}`}>
                    {selectedOrder.status.replace('_', ' ')}
                  </span>
                  {selectedOrder.orderType === 'custom' && (
                    <span style={{ padding: '6px 12px', background: '#8B5CF6', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                      CUSTOM ORDER
                    </span>
                  )}
                  {selectedOrder.orderType === 'custom' && !selectedOrder.pricingApproved && (
                    <span style={{ padding: '6px 12px', background: '#F59E0B', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                      PRICING PENDING
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {selectedOrder.status === 'rejected' && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditOrder(selectedOrder)}
                        data-testid="edit-rejected-order-button"
                      >
                        <Edit size={16} />
                        Edit Order
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ background: '#4CAF50' }}
                        onClick={() => handleApprove(selectedOrder.id)}
                        data-testid="approve-rejected-order-button"
                      >
                        <Check size={16} />
                        Approve
                      </button>
                    </>
                  )}
                  {selectedOrder.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditOrder(selectedOrder)}
                        data-testid="edit-pending-order-button"
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ background: '#4CAF50' }}
                        onClick={() => handleApprove(selectedOrder.id)}
                        data-testid="approve-order-button"
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReject(selectedOrder.id)}
                        data-testid="reject-order-button"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {['approved','in_process'].includes(selectedOrder.status) && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditOrder(selectedOrder)}
                        data-testid="edit-assignment-button"
                      >
                        <Edit size={16} />
                        Assign Installer
                      </button>
                      {(user?.role === 'admin' || user?.role === 'installer') && selectedOrder.status !== 'completed' && (
                        <button
                          className="btn btn-primary"
                          style={{ background: '#4CAF50' }}
                          onClick={() => handleMarkCompleted(selectedOrder.id)}
                          data-testid="mark-completed-button"
                        >
                          <Check size={16} />
                          Mark Completed
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Customer Information</h3>
                <div style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px' }}>
                  <p><strong>Name:</strong> {selectedOrder.customer.name}</p>
                  <p><strong>Email:</strong> {selectedOrder.customer.email}</p>
                  <p><strong>Phone:</strong> {selectedOrder.customer.phone}</p>
                  <p><strong>Address:</strong> {selectedOrder.customer.address}</p>
                  <p><strong>Installation Date:</strong> {formatDate(selectedOrder.installationDate)}</p>
                  {selectedOrder.storeInvoiceNumber && (
                    <p><strong>Store Invoice #:</strong> {selectedOrder.storeInvoiceNumber}</p>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Sales Person</h3>
                <div style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px' }}>
                  <p><strong>Email:</strong> {selectedOrder.createdByName}</p>
                  <p><strong>Order Created:</strong> {new Date(selectedOrder.createdAt).toLocaleString()}</p>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Items</h3>
                {selectedOrder.items.map((item, idx) => (
                  <div key={idx} style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600' }}>{item.name}</span>
                      <span style={{ fontWeight: '700' }}>
                        {item.price !== null ? `$${item.price} x ${item.qty}` : 'Price pending'}
                      </span>
                    </div>
                    {item.notes && (
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', padding: '8px', background: 'white', borderRadius: '6px' }}>
                        <strong>Notes:</strong> {item.notes}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#666' }}>Spiff: {item.spiff !== null ? `$${item.spiff}` : 'Pending'}</div>
                  </div>
                ))}
              </div>

              {selectedOrder.notes && (
                <div style={{ marginBottom: '20px', padding: '16px', background: '#FFF9E6', borderRadius: '12px', border: '1px solid #FDB515' }}>
                  <strong style={{ fontSize: '14px' }}>Order Notes (Sales Person):</strong>
                  <div style={{ marginTop: '8px', color: '#333', whiteSpace: 'pre-wrap' }}>{selectedOrder.notes}</div>
                </div>
              )}

              {selectedOrder.signature_url && (
                <div style={{ marginBottom: '20px', padding: '16px', background: '#FAFAFA', borderRadius: '12px' }}>
                  <strong style={{ fontSize: '14px' }}>Customer Signature:</strong>
                  {selectedOrder.signature_customer_name && (
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#374151' }}>
                      Signed by: {selectedOrder.signature_customer_name}
                    </div>
                  )}
                  {selectedOrder.completed_at && (
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>
                      Completed at: {new Date(selectedOrder.completed_at).toLocaleString()}
                    </div>
                  )}
                  <SignatureImage
                    src={`${API}/orders/${selectedOrder.id}/signature-image`}
                    alt="Customer signature"
                    style={{ marginTop: '10px', width: '100%', maxWidth: '420px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#fff' }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '16px', display:'flex', gap:'12px', alignItems:'center' }}>
                <div style={{ fontWeight:'700' }}>Assigned Installer:</div>
                <div>{selectedOrder.assignedInstallerName || 'Not assigned'}</div>
              </div>

              <div style={{ background: '#FDB515', padding: '20px', borderRadius: '12px', color: '#000' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600' }}>Subtotal:</span>
                  <span style={{ fontWeight: '700' }}>{selectedOrder.totals.subtotal !== null ? `$${selectedOrder.totals.subtotal}` : 'Pending'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600' }}>Total Spiff:</span>
                  <span style={{ fontWeight: '700' }}>{selectedOrder.totals.spiffTotal !== null ? `$${selectedOrder.totals.spiffTotal}` : 'Pending'}</span>
                </div>
                <div style={{ borderTop: '2px solid rgba(0,0,0,0.2)', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '18px', fontWeight: '800' }}>Total:</span>
                  <span style={{ fontSize: '18px', fontWeight: '800' }}>{selectedOrder.totals.total !== null ? `$${selectedOrder.totals.total}` : 'Pending'}</span>
                </div>
              </div>

              {selectedOrder.adminNotes && (
                <div style={{ marginTop: '20px', padding: '16px', background: '#FEE2E2', borderRadius: '12px' }}>
                  <strong>Admin Notes:</strong> {selectedOrder.adminNotes}
                </div>
              )}
            </div>
          )}

          {/* Edit Mode */}
          {selectedOrder && editMode && (
            <div>
              <div style={{ marginBottom: '20px', padding: '12px', background: '#E3F2FD', borderRadius: '8px' }}>
                <strong>Status:</strong> <span className={`badge ${getBadgeClass(selectedOrder.status)}`}>{selectedOrder.status.replace('_', ' ')}</span>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Customer Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Customer Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.customerName}
                      onChange={(e) => setEditFormData({ ...editFormData, customerName: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={editFormData.customerEmail}
                      onChange={(e) => setEditFormData({ ...editFormData, customerEmail: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={editFormData.customerPhone}
                      onChange={(e) => setEditFormData({ ...editFormData, customerPhone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Installation Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={editFormData.installationDate}
                      onChange={(e) => setEditFormData({ ...editFormData, installationDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Store Invoice #</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFormData.storeInvoiceNumber}
                      onChange={(e) => setEditFormData({ ...editFormData, storeInvoiceNumber: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <textarea
                    className="form-input"
                    value={editFormData.customerAddress}
                    onChange={(e) => setEditFormData({ ...editFormData, customerAddress: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700' }}>Items</h3>
                  <button type="button" onClick={addItem} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '13px' }}>
                    <Plus size={14} />
                    Add Item
                  </button>
                </div>

                {editFormData.items.map((item, index) => (
                  <div key={index} style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Category</label>
                        <Select value={item.productId} onValueChange={(value) => updateItem(index, 'productId', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map(product => (
                              <SelectItem key={product.id} value={product.id}>{product.category}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Service</label>
                        <Select value={item.serviceId} onValueChange={(value) => updateItem(index, 'serviceId', value)} disabled={!item.productId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {item.productId && products.find(p => p.id === item.productId)?.services.map(service => (
                              <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Qty</label>
                        <input
                          type="number"
                          className="form-input"
                          value={item.qty}
                          onChange={(e) => updateItem(index, 'qty', parseInt(e.target.value) || 1)}
                          min={1}
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Price</label>
                        {selectedOrder.orderType === 'custom' ? (
                          <input
                            type="number"
                            className="form-input"
                            value={item.price !== null ? item.price : ''}
                            onChange={(e) => {
                              const newItems = [...editFormData.items];
                              newItems[index].price = e.target.value ? parseFloat(e.target.value) : null;
                              setEditFormData({ ...editFormData, items: newItems });
                            }}
                            placeholder="Set price"
                            step="0.01"
                            min="0"
                            style={{ fontSize: '13px', padding: '8px' }}
                          />
                        ) : (
                          <div style={{ padding: '8px', background: 'white', borderRadius: '8px', fontWeight: '600', fontSize: '13px' }}>
                            ${item.price}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="btn btn-danger"
                        style={{ padding: '8px' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    {item.notes && (
                      <div style={{ marginTop: '8px', padding: '8px', background: 'white', borderRadius: '6px' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Notes</div>
                        <div style={{ fontSize: '12px', color: '#333' }}>{item.notes}</div>
                      </div>
                    )}
                    
                    {selectedOrder.orderType === 'custom' ? (
                      <div style={{ marginTop: '8px' }}>
                        <label className="form-label" style={{ fontSize: '12px' }}>Spiff</label>
                        <input
                          type="number"
                          className="form-input"
                          value={item.spiff !== null ? item.spiff : ''}
                          onChange={(e) => {
                            const newItems = [...editFormData.items];
                            newItems[index].spiff = e.target.value ? parseFloat(e.target.value) : null;
                            setEditFormData({ ...editFormData, items: newItems });
                          }}
                          placeholder="Set spiff"
                          step="0.01"
                          min="0"
                          style={{ fontSize: '13px', padding: '8px' }}
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>Spiff: ${item.spiff}</div>
                    )}
                  </div>
                ))}
              </div>

              {selectedOrder.notes && (
                <div style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Order Notes (from Sales Person)</label>
                    <div style={{ padding: '12px', background: '#FFF9E6', borderRadius: '8px', border: '1px solid #FDB515', whiteSpace: 'pre-wrap' }}>
                      {selectedOrder.notes}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <div className="form-group">
                  <label className="form-label">Admin Notes</label>
                  <textarea
                    className="form-input"
                    value={editFormData.adminNotes}
                    onChange={(e) => setEditFormData({ ...editFormData, adminNotes: e.target.value })}
                    rows={3}
                    placeholder="Add notes about this order..."
                  />
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
                <div className="form-group">
                  <label className="form-label">Assign Installer</label>
                  <select
                    className="form-input"
                    value={editFormData.assignedInstallerId}
                    onChange={(e)=> setEditFormData({ ...editFormData, assignedInstallerId: e.target.value })}
                  >
                    <option value="">Not assigned</option>
                    {installers.map(inst => (
                      <option key={inst.id} value={inst.id}>{inst.name || inst.email}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ background: '#FDB515', padding: '16px', borderRadius: '12px', color: '#000', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600' }}>Subtotal:</span>
                  <span style={{ fontWeight: '700' }}>
                    {editFormData.items.every(item => item.price !== null) 
                      ? `$${editFormData.items.reduce((sum, item) => sum + (item.price * item.qty), 0).toFixed(2)}`
                      : 'Pending (set all prices)'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600' }}>Total Spiff:</span>
                  <span style={{ fontWeight: '700' }}>
                    {editFormData.items.every(item => item.spiff !== null)
                      ? `$${editFormData.items.reduce((sum, item) => sum + (item.spiff * item.qty), 0).toFixed(2)}`
                      : 'Pending (set all spiffs)'}
                  </span>
                </div>
                <div style={{ borderTop: '2px solid rgba(0,0,0,0.2)', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '16px', fontWeight: '800' }}>Total:</span>
                  <span style={{ fontSize: '16px', fontWeight: '800' }}>
                    {editFormData.items.every(item => item.price !== null)
                      ? `$${editFormData.items.reduce((sum, item) => sum + (item.price * item.qty), 0).toFixed(2)}`
                      : 'Pending'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  onClick={handleSaveOrder}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                {selectedOrder.orderType === 'custom' && !selectedOrder.pricingApproved && (
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1, background: '#8B5CF6' }}
                    onClick={async () => {
                      const allPricesSet = editFormData.items.every(item => item.price !== null && item.spiff !== null);
                      if (!allPricesSet) {
                        toast.error('Please set price and spiff for all items before approving');
                        return;
                      }
                      await handleSaveOrder();
                      if (!saving) {
                        await handleApprove(selectedOrder.id);
                      }
                    }}
                    disabled={saving}
                  >
                    Approve Pricing & Order
                  </button>
                )}
                {selectedOrder.status === 'rejected' && (
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1, background: '#4CAF50' }}
                    onClick={async () => {
                      await handleSaveOrder();
                      if (!saving) {
                        await handleApprove(selectedOrder.id);
                      }
                    }}
                    disabled={saving}
                  >
                    <Check size={16} />
                    Save & Approve
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SignatureCaptureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        onSave={handleSignatureSave}
        saving={savingSignature}
        defaultCustomerName={selectedOrder?.customer?.name || ''}
      />
    </div>
  );
};

export default Orders;
